import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { ModuleRoute } from '@core/router/types';
import { Permissions } from '@core/auth/permissions';

const EmployeesListPage = lazy(() => import('./pages/EmployeesListPage'));
const EmployeeFormPage = lazy(() => import('./pages/EmployeeFormPage'));
const EmployeeDetailsPage = lazy(() => import('./pages/EmployeeDetailsPage'));
const AbsencesManagementPage = lazy(() => import('./pages/AbsencesManagementPage'));
const OrganizationalStructurePage = lazy(() => import('./pages/OrganizationalStructurePage'));
const JobPositionsListPage = lazy(() => import('./pages/JobPositionsListPage'));
const JobPositionFormPage = lazy(() => import('./pages/JobPositionFormPage'));
const DocumentTemplatesPage = lazy(() => import('./pages/DocumentTemplatesPage'));
const EmployeeDocumentDraftEditorPage = lazy(() => import('./pages/EmployeeDocumentDraftEditorPage'));

// Reports (Izveštaji)
const ReportsPage = lazy(() => import('./pages/ReportsPage'));

// Catalogs (Katalozi) - all CRUD via inline modal dialogs
const EducationCatalogsPage = lazy(() => import('./pages/EducationCatalogsPage'));
const EmploymentTypesListPage = lazy(() => import('./pages/EmploymentTypesListPage'));
const DocumentTypesListPage = lazy(() => import('./pages/DocumentTypesListPage'));
const AbsenceTypesListPage = lazy(() => import('./pages/AbsenceTypesListPage'));
const EmploymentStartReasonsListPage = lazy(() => import('./pages/EmploymentStartReasonsListPage'));
const EmploymentEndReasonsListPage = lazy(() => import('./pages/EmploymentEndReasonsListPage'));
const LeaveEntitlementRulesListPage = lazy(() => import('./pages/LeaveEntitlementRulesListPage'));
const PaidLeaveCriteriaListPage = lazy(() => import('./pages/PaidLeaveCriteriaListPage'));
const OfficialJobsListPage = lazy(() => import('./pages/OfficialJobsListPage'));
const NationalitiesListPage = lazy(() => import('./pages/NationalitiesListPage'));
const CitizenshipsListPage = lazy(() => import('./pages/CitizenshipsListPage'));
const ReligionsListPage = lazy(() => import('./pages/ReligionsListPage'));
const PatronSaintsListPage = lazy(() => import('./pages/PatronSaintsListPage'));
const PublicHolidaysListPage = lazy(() => import('./pages/PublicHolidaysListPage'));

/**
 * Human Resources Module Routes
 *
 * All routes are relative to /ljudski-resursi base path.
 */
export const humanResourcesRoutes: ModuleRoute[] = [
  {
    index: true,
    element: <Navigate to="zaposleni" replace />,
  },

  // Employees
  {
    path: 'zaposleni',
    element: <EmployeesListPage />,
    permission: Permissions.HumanResources.Employees.Read,
    meta: {
      title: 'Zaposleni',
      icon: 'Badge',
      showInSidebar: true,
      order: 1,
    },
  },
  {
    path: 'zaposleni/novi',
    element: <EmployeeFormPage />,
    permission: Permissions.HumanResources.Employees.Create,
    meta: {
      title: 'Novi zaposleni',
      showInSidebar: false,
    },
  },
  {
    path: 'zaposleni/:id',
    element: <EmployeeDetailsPage />,
    permission: Permissions.HumanResources.Employees.Read,
    meta: {
      title: 'Detalji zaposlenog',
      showInSidebar: false,
    },
  },
  {
    path: 'zaposleni/:id/izmeni',
    element: <EmployeeFormPage />,
    permission: Permissions.HumanResources.Employees.Update,
    meta: {
      title: 'Izmeni zaposlenog',
      showInSidebar: false,
    },
  },
  {
    path: 'zaposleni/:employeeId/dokumenti/:documentId/uredi',
    element: <EmployeeDocumentDraftEditorPage />,
    permission: Permissions.HumanResources.EmployeeDocuments.Read,
    meta: {
      title: 'Izmena dokumenta',
      showInSidebar: false,
    },
  },

  // Annual Leave & Absences (Godišnji odmori i odsustva) — cross-employee
  {
    path: 'odsustva-i-odmori',
    element: <AbsencesManagementPage />,
    permission: Permissions.HumanResources.EmployeeAbsences.Read,
    meta: {
      title: 'Godišnji odmori i odsustva',
      icon: 'EventBusy',
      showInSidebar: true,
      order: 2,
    },
  },

  // Work Units (Organizaciona struktura)
  {
    path: 'organizaciona-struktura',
    element: <OrganizationalStructurePage />,
    permission: Permissions.HumanResources.WorkUnits.Read,
    meta: {
      title: 'Organizaciona struktura',
      icon: 'AccountTree',
      showInSidebar: true,
      order: 3,
    },
  },
  // Job Positions (Sistematizacija)
  {
    path: 'sistematizacija',
    element: <JobPositionsListPage />,
    permission: Permissions.HumanResources.JobPositions.Read,
    meta: {
      title: 'Sistematizacija',
      icon: 'WorkOutline',
      showInSidebar: true,
      order: 4,
    },
  },
  {
    path: 'sistematizacija/novo',
    element: <JobPositionFormPage />,
    permission: Permissions.HumanResources.JobPositions.Manage,
    meta: {
      title: 'Novo radno mesto',
      showInSidebar: false,
    },
  },
  {
    path: 'sistematizacija/:id/izmeni',
    element: <JobPositionFormPage />,
    permission: Permissions.HumanResources.JobPositions.Manage,
    meta: {
      title: 'Izmeni radno mesto',
      showInSidebar: false,
    },
  },
  {
    path: 'dokumenti',
    element: <DocumentTemplatesPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: {
      title: 'HR dokumenta',
      icon: 'Description',
      showInSidebar: true,
      order: 5,
    },
  },
  // Catalogs (Katalozi) - all guarded by Hr.Catalogs.Manage; CRUD via modal dialogs
  {
    path: 'katalozi/obrazovanje',
    element: <EducationCatalogsPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Obrazovanje', showInSidebar: false },
  },
  {
    path: 'katalozi/strucne-spreme',
    element: <Navigate to="/ljudski-resursi/katalozi/obrazovanje?tab=spreme" replace />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Stručne spreme', showInSidebar: false },
  },
  {
    path: 'katalozi/stepeni-obrazovanja',
    element: <Navigate to="/ljudski-resursi/katalozi/obrazovanje?tab=stepeni" replace />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Stepeni obrazovanja', showInSidebar: false },
  },
  {
    path: 'katalozi/tipovi-dokumenata',
    element: <DocumentTypesListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Tipovi dokumenata', showInSidebar: false },
  },
  {
    path: 'katalozi/vrste-radnog-odnosa',
    element: <EmploymentTypesListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Vrste radnog odnosa', showInSidebar: false },
  },
  {
    path: 'katalozi/vrste-odsustva',
    element: <AbsenceTypesListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Vrste odsustva', showInSidebar: false },
  },
  {
    path: 'katalozi/kriterijumi-godisnjeg-odmora',
    element: <LeaveEntitlementRulesListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Kriterijumi godišnjeg odmora', showInSidebar: false },
  },
  {
    path: 'katalozi/kriterijumi-placenog-odsustva',
    element: <PaidLeaveCriteriaListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Kriterijumi plaćenog odsustva', showInSidebar: false },
  },
  {
    path: 'katalozi/razlozi-zasnivanja',
    element: <EmploymentStartReasonsListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Razlozi zasnivanja radnog odnosa', showInSidebar: false },
  },
  {
    path: 'katalozi/razlozi-prestanka',
    element: <EmploymentEndReasonsListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Razlozi prestanka radnog odnosa', showInSidebar: false },
  },
  {
    path: 'katalozi/sluzbena-zanimanja',
    element: <OfficialJobsListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Službena zanimanja', showInSidebar: false },
  },
  {
    path: 'katalozi/nacionalnosti',
    element: <NationalitiesListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Nacionalnosti', showInSidebar: false },
  },
  {
    path: 'katalozi/drzavljanstva',
    element: <CitizenshipsListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Državljanstva', showInSidebar: false },
  },
  {
    path: 'katalozi/veroispovesti',
    element: <ReligionsListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Veroispovesti', showInSidebar: false },
  },
  {
    path: 'katalozi/slave',
    element: <PatronSaintsListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Slave', showInSidebar: false },
  },
  {
    path: 'katalozi/drzavni-praznici',
    element: <PublicHolidaysListPage />,
    permission: Permissions.HumanResources.Catalogs.Manage,
    meta: { title: 'Državni praznici', showInSidebar: false },
  },

  // Reports
  {
    path: 'izvestaji',
    element: <ReportsPage />,
    permission: Permissions.HumanResources.Reports.Run,
    meta: {
      title: 'Izveštaji',
      icon: 'Assessment',
      showInSidebar: true,
      order: 6,
    },
  },
];
